import { useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import {
  PatientReportRequest,
  AggregateReportRequest,
  MeasurementAnalysisReportRequest,
  ReportFormat,
} from '../types/report'
import { reportService } from '../services/reportService'
import './ReportBuilder.css'

interface ReportBuilderProps {
  patientId?: string
  onReportGenerated?: () => void
  onCancel?: () => void
}

// Available fields for field selection, grouped by category
const AVAILABLE_FIELDS_BY_CATEGORY = {
  demographics: [
    { id: 'patient', label: 'Patient Demographics' },
  ],
  clinical: [
    { id: 'consultations', label: 'Consultations' },
    { id: 'prescriptions', label: 'Prescriptions' },
    { id: 'labResults', label: 'Lab Results' },
    { id: 'vitalSigns', label: 'Vital Signs' },
    { id: 'allergies', label: 'Allergies' },
    { id: 'immunisations', label: 'Immunisations' },
    { id: 'medications', label: 'Medications' },
    { id: 'clinicalHistory', label: 'Clinical History' },
  ],
  administrative: [
    { id: 'appointments', label: 'Appointments' },
    { id: 'audit', label: 'Audit Trail' },
  ],
}

const MEASUREMENT_MIN_FIELDS = [
  { id: 'patientId', label: 'Patient ID (pseudonymous)' },
  { id: 'age', label: 'Age' },
  { id: 'sex', label: 'Sex' },
  { id: 'gmsStatus', label: 'GMS Status' },
  { id: 'county', label: 'County' },
  { id: 'postcode', label: 'Postcode' },
  { id: 'consultationDate', label: 'Consultation Date' },
  { id: 'diagnosisCode', label: 'Diagnosis Code' },
  { id: 'diagnosisText', label: 'Diagnosis Text' },
]

const MEASUREMENT_OPTIONAL_FIELDS = [
  { id: 'symptoms', label: 'Symptoms' },
  { id: 'reasonForEncounter', label: 'Reason for Encounter' },
  { id: 'medicationsPrescribed', label: 'Medications Prescribed' },
  { id: 'procedures', label: 'Procedures' },
  { id: 'treatments', label: 'Treatments' },
  { id: 'investigations', label: 'Investigations' },
  { id: 'referrals', label: 'Referrals' },
  { id: 'testsOrdered', label: 'Tests Ordered' },
  { id: 'outcomeMeasures', label: 'Outcome Measures' },
  { id: 'staffId', label: 'Staff ID' },
  { id: 'staffName', label: 'Staff Name' },
  { id: 'staffRole', label: 'Staff Role' },
]

const CATEGORY_LABELS = {
  demographics: 'Demographics',
  clinical: 'Clinical Data',
  administrative: 'Administrative',
}

// Available sort fields
const SORT_FIELDS = [
  { value: 'patient.lastName', label: 'Patient Last Name' },
  { value: 'patient.firstName', label: 'Patient First Name' },
  { value: 'patient.dateOfBirth', label: 'Date of Birth' },
  { value: 'consultation.consultationDate', label: 'Consultation Date' },
  { value: 'appointment.appointmentDate', label: 'Appointment Date' },
  { value: 'prescription.prescriptionDate', label: 'Prescription Date' },
  { value: 'labResult.resultDate', label: 'Lab Result Date' },
]

export function ReportBuilder({ patientId, onReportGenerated, onCancel }: ReportBuilderProps) {
  const [reportType, setReportType] = useState<'patient' | 'aggregate' | 'measurement'>(
    patientId ? 'patient' : 'aggregate'
  )
  const [format, setFormat] = useState<ReportFormat>('json')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [showFieldSelection, setShowFieldSelection] = useState(false)
  const [showSorting, setShowSorting] = useState(false)

  const { register, handleSubmit, setValue, control } = useForm({
    defaultValues: {
      includeLinked: false,
      maskPII: false,
      format: 'json' as ReportFormat,
      filters: {
        demographics: {
          ageRange: { min: undefined, max: undefined },
          gender: '',
          dateOfBirthRange: { start: '', end: '' },
        },
        clinical: {
          conditions: [] as string[],
          medications: [] as string[],
          allergies: [] as string[],
        },
        dateRange: {
          startDate: '',
          endDate: '',
        },
      },
      measurementFilters: {
        diagnoses: '',
        staffRole: '',
        staffId: '',
      },
      measurementOptions: {
        includeOutcomes: false,
      },
      sorting: [] as Array<{ field: string; direction: 'asc' | 'desc' }>,
      fields: Object.values(AVAILABLE_FIELDS_BY_CATEGORY).flat().map(f => f.id) as string[], // Default: all fields selected
      measurementFields: MEASUREMENT_OPTIONAL_FIELDS.map(field => field.id),
    },
  })

  useEffect(() => {
    if (reportType === 'measurement' && format === 'fhir') {
      setFormat('csv')
      setValue('format', 'csv')
    }
  }, [reportType, format, setValue])

  const { fields: sortFields, append: appendSort, remove: removeSort } = useFieldArray({
    control,
    name: 'sorting',
  })

  const onSubmit = async (data: any) => {
    setLoading(true)
    setError(null)

    try {
      let blob: Blob
      let filename: string

      // Process filters - convert comma-separated strings to arrays
      const processedFilters = {
        ...data.filters,
        clinical: {
          conditions: data.filters?.clinical?.conditions
            ? (typeof data.filters.clinical.conditions === 'string'
                ? data.filters.clinical.conditions.split(',').map((s: string) => s.trim()).filter(Boolean)
                : data.filters.clinical.conditions)
            : [],
          medications: data.filters?.clinical?.medications
            ? (typeof data.filters.clinical.medications === 'string'
                ? data.filters.clinical.medications.split(',').map((s: string) => s.trim()).filter(Boolean)
                : data.filters.clinical.medications)
            : [],
          allergies: data.filters?.clinical?.allergies
            ? (typeof data.filters.clinical.allergies === 'string'
                ? data.filters.clinical.allergies.split(',').map((s: string) => s.trim()).filter(Boolean)
                : data.filters.clinical.allergies)
            : [],
        },
      }

      // Process sorting - filter out empty fields
      const processedSorting = (data.sorting || []).filter((s: any) => s.field && s.direction)

      // Process fields - filter out empty values
      // Logic: 
      // - If all fields are checked (or empty array), send undefined to include everything
      // - If some fields are checked, send array of checked fields to include only those
      const processedFields = Array.isArray(data.fields)
        ? data.fields.filter((f: any) => f)
        : data.fields
          ? [data.fields]
          : []

      // Get all available fields
      const allFields = Object.values(AVAILABLE_FIELDS_BY_CATEGORY).flat().map(f => f.id)
      
      // Logic: 
      // - If all fields are checked (default state) or empty array, send undefined to include everything
      // - If some fields are checked (user unchecked some), send array of checked fields to include only those
      const allFieldsSelected = processedFields.length === allFields.length &&
                                allFields.every(field => processedFields.includes(field))
      
      const fieldsToSend = (processedFields.length === 0 || allFieldsSelected)
        ? undefined  // Include everything (default state or all selected)
        : processedFields  // Include only the selected fields

      if (reportType === 'patient' && patientId) {
        const request: PatientReportRequest = {
          patientId,
          format: data.format || format,
          includeLinked: data.includeLinked || false,
          maskPII: data.maskPII || false,
          filters: processedFilters,
          sorting: processedSorting,
          fields: fieldsToSend,
        }
        blob = await reportService.generatePatientReport(request)
        filename = `patient-report-${patientId}-${Date.now()}.${data.format === 'fhir' ? 'json' : data.format}`
      } else if (reportType === 'aggregate') {
        const request: AggregateReportRequest = {
          format: data.format || format,
          filters: processedFilters,
          sorting: processedSorting,
          fields: fieldsToSend,
        }
        blob = await reportService.generateAggregateReport(request)
        filename = `aggregate-report-${Date.now()}.${data.format === 'fhir' ? 'json' : data.format}`
      } else {
        const measurementFilters = {
          dateRange: processedFilters.dateRange,
          diagnoses: data.measurementFilters?.diagnoses
            ? String(data.measurementFilters.diagnoses)
                .split(',')
                .map((item: string) => item.trim())
                .filter(Boolean)
            : [],
          staffRole: data.measurementFilters?.staffRole || undefined,
          staffId: data.measurementFilters?.staffId || undefined,
        }

        const measurementFields = Array.isArray(data.measurementFields)
          ? data.measurementFields.filter((field: string) => field)
          : data.measurementFields
            ? [data.measurementFields]
            : []

        const request: MeasurementAnalysisReportRequest = {
          format: data.format || format,
          filters: measurementFilters,
          fields: measurementFields,
          includeOutcomes: Boolean(data.measurementOptions?.includeOutcomes),
        }
        blob = await reportService.generateMeasurementAnalysisReport(request)
        filename = `measurement-analysis-report-${Date.now()}.${data.format === 'fhir' ? 'json' : data.format}`
      }

      reportService.downloadReport(blob, filename)
      onReportGenerated?.()
    } catch (err: any) {
      setError(err?.message || 'Failed to generate report')
      console.error('Report generation error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="report-builder">
      <h2>Generate Report</h2>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Report Type */}
        {!patientId && (
          <div className="form-group">
            <label>Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as 'patient' | 'aggregate' | 'measurement')}
            >
              <option value="aggregate">Aggregate Report</option>
              <option value="measurement">Measurement &amp; Analysis Report</option>
            </select>
          </div>
        )}

        {/* Format Selection */}
        <div className="form-group">
          <label>Export Format</label>
          <select
            value={format}
            onChange={(e) => {
              setFormat(e.target.value as ReportFormat)
              setValue('format', e.target.value as ReportFormat)
            }}
          >
            <option value="json">JSON</option>
            {reportType !== 'measurement' && <option value="fhir">FHIR R4</option>}
            <option value="csv">CSV</option>
            <option value="xml">XML</option>
          </select>
        </div>

        {/* Patient Report Options */}
        {reportType === 'patient' && patientId && (
          <>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  {...register('includeLinked')}
                />
                Include Linked Patient Records
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  {...register('maskPII')}
                />
                Mask PII (Personal Identifiable Information)
              </label>
            </div>
          </>
        )}

        {/* Date Range Filter */}
        <div className="form-group">
          <label>Date Range (Optional)</label>
          <div className="date-range">
            <input
              type="date"
              {...register('filters.dateRange.startDate')}
              placeholder="Start Date"
            />
            <input
              type="date"
              {...register('filters.dateRange.endDate')}
              placeholder="End Date"
            />
          </div>
          <small className="form-help">Filter records by date range (applies to consultations, appointments, prescriptions, lab results)</small>
        </div>

        {/* Advanced Filters Toggle */}
        {reportType !== 'measurement' && (
          <>
            <div className="form-group">
              <button
                type="button"
                className="toggle-button"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              >
                {showAdvancedFilters ? '▼' : '▶'} Advanced Filters
              </button>
            </div>

            {showAdvancedFilters && (
              <>
                {/* Demographic Filters */}
                <div className="form-group filter-section">
                  <h3>Demographic Filters</h3>
                  <div className="filter-group">
                    <label>Gender</label>
                    <select {...register('filters.demographics.gender')}>
                      <option value="">All</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Age Range</label>
                    <div className="age-range">
                      <input
                        type="number"
                        {...register('filters.demographics.ageRange.min', { valueAsNumber: true })}
                        placeholder="Min Age"
                        min="0"
                      />
                      <input
                        type="number"
                        {...register('filters.demographics.ageRange.max', { valueAsNumber: true })}
                        placeholder="Max Age"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="filter-group">
                    <label>Date of Birth Range</label>
                    <div className="date-range">
                      <input
                        type="date"
                        {...register('filters.demographics.dateOfBirthRange.start')}
                        placeholder="Start Date"
                      />
                      <input
                        type="date"
                        {...register('filters.demographics.dateOfBirthRange.end')}
                        placeholder="End Date"
                      />
                    </div>
                  </div>
                </div>

                {/* Clinical Filters */}
                <div className="form-group filter-section">
                  <h3>Clinical Filters</h3>
                  <div className="filter-group">
                    <label>Conditions/Diagnoses (comma-separated)</label>
                    <input
                      type="text"
                      {...register('filters.clinical.conditions')}
                      placeholder="e.g., Diabetes, Hypertension"
                    />
                    <small className="form-help">Enter conditions separated by commas</small>
                  </div>
                  <div className="filter-group">
                    <label>Medications (comma-separated)</label>
                    <input
                      type="text"
                      {...register('filters.clinical.medications')}
                      placeholder="e.g., Metformin, Aspirin"
                    />
                    <small className="form-help">Enter medication names separated by commas</small>
                  </div>
                  <div className="filter-group">
                    <label>Allergies (comma-separated)</label>
                    <input
                      type="text"
                      {...register('filters.clinical.allergies')}
                      placeholder="e.g., Penicillin, Latex"
                    />
                    <small className="form-help">Enter allergens separated by commas</small>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {reportType === 'measurement' && (
          <div className="form-group filter-section">
            <h3>Measurement Filters</h3>
            <div className="filter-group">
              <label>Diagnoses (comma-separated)</label>
              <input
                type="text"
                {...register('measurementFilters.diagnoses')}
                placeholder="e.g., E11.9, Hypertension"
              />
              <small className="form-help">Use diagnosis codes or text, separated by commas.</small>
            </div>
            <div className="filter-group">
              <label>Staff Role</label>
              <select {...register('measurementFilters.staffRole')}>
                <option value="">All</option>
                <option value="doctor">GP/Doctor</option>
                <option value="nurse">Practice Nurse</option>
                <option value="physician-assistant">Physician Assistant</option>
                <option value="therapist">Therapist</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Staff ID (Optional)</label>
              <input
                type="text"
                {...register('measurementFilters.staffId')}
                placeholder="Enter staff ID"
              />
            </div>
            <div className="filter-group">
              <label>
                <input
                  type="checkbox"
                  {...register('measurementOptions.includeOutcomes')}
                />
                Include outcome measures from care plans
              </label>
            </div>
          </div>
        )}

        {/* Field Selection Toggle */}
        <div className="form-group">
          <button
            type="button"
            className="toggle-button"
            onClick={() => setShowFieldSelection(!showFieldSelection)}
          >
            {showFieldSelection ? '▼' : '▶'} Field Selection (Optional)
          </button>
          <small className="form-help">
            {reportType === 'measurement'
              ? 'Minimum dataset is always included. Optional fields can be added below.'
              : 'All sections are included by default. Uncheck sections to exclude them.'}
          </small>
        </div>

        {/* Field Selection */}
        {showFieldSelection && reportType !== 'measurement' && (
          <div className="form-group filter-section">
            <h3>Select Data Sections</h3>
            <p className="section-description">
              All sections are included by default. Uncheck sections to exclude them from the report.
            </p>
            <div className="field-selection-grouped">
              {Object.entries(AVAILABLE_FIELDS_BY_CATEGORY).map(([category, fields]) => (
                <div key={category} className="field-category-group">
                  <h4 className="category-header">{CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}</h4>
                  <div className="field-selection">
                    {fields.map((field) => (
                      <label key={field.id} className="field-checkbox">
                        <input
                          type="checkbox"
                          {...register(`fields`)}
                          value={field.id}
                          className="field-checkbox-input"
                        />
                        <span className="field-label">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="field-selection-actions">
              <button
                type="button"
                onClick={() => {
                  const allFields = Object.values(AVAILABLE_FIELDS_BY_CATEGORY).flat().map(f => f.id)
                  allFields.forEach(fieldId => {
                    const checkbox = document.querySelector(`input[type="checkbox"][value="${fieldId}"]`) as HTMLInputElement
                    if (checkbox) checkbox.checked = true
                  })
                  // Update form state - all checked means include everything (send all fields, which will be converted to undefined)
                  setValue('fields', allFields)
                }}
                className="select-all-button"
              >
                Select All (Include Everything)
              </button>
            </div>
          </div>
        )}

        {showFieldSelection && reportType === 'measurement' && (
          <div className="form-group filter-section">
            <h3>Minimum Dataset (Always Included)</h3>
            <div className="field-selection">
              {MEASUREMENT_MIN_FIELDS.map((field) => (
                <label key={field.id} className="field-checkbox">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="field-checkbox-input"
                  />
                  <span className="field-label">{field.label}</span>
                </label>
              ))}
            </div>
            <h3>Optional Fields</h3>
            <div className="field-selection">
              {MEASUREMENT_OPTIONAL_FIELDS.map((field) => (
                <label key={field.id} className="field-checkbox">
                  <input
                    type="checkbox"
                    {...register('measurementFields')}
                    value={field.id}
                    className="field-checkbox-input"
                  />
                  <span className="field-label">{field.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {reportType !== 'measurement' && (
          <>
            {/* Sorting Toggle */}
            <div className="form-group">
              <button
                type="button"
                className="toggle-button"
                onClick={() => setShowSorting(!showSorting)}
              >
                {showSorting ? '▼' : '▶'} Sorting (Optional)
              </button>
              <small className="form-help">Specify how to sort the report data</small>
            </div>

            {/* Sorting Options */}
            {showSorting && (
              <div className="form-group filter-section">
                <h3>Sort Options</h3>
                {sortFields.map((field, index) => (
                  <div key={field.id} className="sort-row">
                    <select {...register(`sorting.${index}.field` as const)}>
                      <option value="">Select field...</option>
                      {SORT_FIELDS.map((sf) => (
                        <option key={sf.value} value={sf.value}>
                          {sf.label}
                        </option>
                      ))}
                    </select>
                    <select {...register(`sorting.${index}.direction` as const)}>
                      <option value="asc">Ascending</option>
                      <option value="desc">Descending</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeSort(index)}
                      className="remove-button"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => appendSort({ field: '', direction: 'asc' })}
                  className="add-button"
                >
                  + Add Sort Field
                </button>
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
